import React, {useState, useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {
    Container,
    TextField,
    Button,
    Typography,
    Paper,
    Snackbar,
    Alert,
    Link,
    Box,
} from '@mui/material';
import {createMfaBearerClient} from '../mfaClient';
import {useAuth} from '../AuthContext';

function MfaChallenge() {
    const [code, setCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [errorOpen, setErrorOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const {applyAccessToken} = useAuth();
    const mfaToken = location.state?.mfaToken;

    useEffect(() => {
        if (!mfaToken) {
            navigate('/login', {replace: true});
        }
    }, [mfaToken, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!mfaToken) {
            return;
        }
        setSubmitting(true);
        setErrorOpen(false);
        try {
            const client = createMfaBearerClient(mfaToken);
            const response = await client.post('/mfa/verify', {code: code.trim()});
            const {token} = response.data;
            applyAccessToken(token);
            navigate('/', {replace: true});
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Verification failed.');
            setErrorOpen(true);
        } finally {
            setSubmitting(false);
        }
    };

    if (!mfaToken) {
        return null;
    }

    return (
        <Container component="main" maxWidth="xs" className="component-container">
            <Paper elevation={3} sx={{p: 3, mt: 4}}>
                <Typography component="h1" variant="h5" gutterBottom>
                    Two-factor authentication
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    Enter the 6-digit code from your authenticator app, or a recovery code.
                </Typography>
                <form onSubmit={handleSubmit}>
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        label="Code"
                        name="code"
                        autoComplete="one-time-code"
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{mt: 2}}
                        disabled={submitting}
                    >
                        {submitting ? 'Verifying…' : 'Continue'}
                    </Button>
                    <Box sx={{mt: 2, textAlign: 'center'}}>
                        <Link component="button" type="button" variant="body2" onClick={() => navigate('/login')}>
                            Back to sign in
                        </Link>
                    </Box>
                </form>
            </Paper>
            <Snackbar open={errorOpen} autoHideDuration={6000} onClose={() => setErrorOpen(false)} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert severity="error" onClose={() => setErrorOpen(false)} sx={{width: '100%'}}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default MfaChallenge;
